import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  fetchMyAsks,
  fetchStudioAsks,
  groupAsks,
  MY_ASKS_KEY,
  STUDIO_ASK_CAPABILITIES,
  STUDIO_ASKS_KEY,
  type AskRow as AskRowData,
} from '../api/asks'
import { fetchContent, text } from '../api/content'
import { useSession } from '../useSession'
import AskRow from './AskRow'
import AssignTaskDialog from './AssignTaskDialog'
import WeekendHoldsPanel from './WeekendHoldsPanel'

type Content = Parameters<typeof text>[0]

/**
 * Everything waiting on somebody's answer, in one place (D-543).
 *
 * **One screen for seven kinds of waiting**, where there were four screens for
 * the studio and a fifth for a teacher, each answering "who may answer, what is
 * late, who is told" its own way. The spec is
 * `docs/specs/2026-09-07-one-queue-for-every-ask.md`.
 *
 * **Two lists, three groups.** `/api/asks/mine` is what this person holds or
 * raised; `/api/asks/studio` is what the studio holds by capability, may step
 * into because it is late, or can only chase. The studio list is asked for only
 * by an account that can read it, because every chase row carries a family's
 * name and number.
 *
 * **Each half fails on its own** (D-209). The studio's list refusing for
 * seventy-five seconds after a deploy must not hide a teacher's own rows, and
 * "nothing waiting" is only said when both halves have answered.
 *
 * **This lists; the row opens where the answer is given.** Until each kind is
 * migrated onto the envelope its answer lives on the screen it always had, and
 * a queue offering a control it cannot honour is D-204's control that lies.
 */
export default function AsksView() {
  const { data: content } = useQuery({ queryKey: ['content'], queryFn: fetchContent })
  const { session } = useSession()

  const readsTheStudio = STUDIO_ASK_CAPABILITIES.some((c) => session?.capabilities.includes(c) ?? false)

  // Staff hand each other tasks (D-549); a parent has no list here at all.
  const isStaff = Boolean(session?.staffId)
  const [assigning, setAssigning] = useState(false)

  const mine = useQuery({ queryKey: MY_ASKS_KEY, queryFn: fetchMyAsks })
  const studio = useQuery({ queryKey: STUDIO_ASKS_KEY, queryFn: fetchStudioAsks, enabled: readsTheStudio })

  const groups = groupAsks(mine.data ?? [], studio.data ?? null)

  const loading = mine.isPending || (readsTheStudio && studio.isPending)
  const answered = !loading && !mine.isError && !(readsTheStudio && studio.isError)
  const nothing =
    answered && groups.waitingOnYou.length + groups.chase.length + groups.youAreWaitingOn.length === 0

  return (
    <div className="page-shell px-5 py-8">
      {/*
        `page-shell` is width and centring only; the gutter is the screen's
        own, the same `px-5 py-8` every other signed-in screen adds beside it
        (D-225). It shipped bare, flush to the sidebar and to a phone's edge -
        the owner saw it in the first screenshots.

        No back link (D-594): Requests is a rail destination (D-560), and
        D-353's control is for screens the rail does not carry.
      */}
      <h1 className="font-display text-2xl">{text(content, 'asks.heading', 'Requests')}</h1>

      <p className="mt-1 mb-4 max-w-[52ch] text-[13px] text-ink-2">
        {text(
          content,
          'asks.intro',
          'Everything waiting on an answer, in one place. Open a row to answer it where it lives.',
        )}
      </p>

      {isStaff && (
        <div className="mb-4">
          <button
            type="button"
            onClick={() => setAssigning(true)}
            className="rounded-lg border border-line-2 px-3.5 py-2 text-[12.5px] font-semibold text-ink-2 hover:border-accent hover:text-accent"
          >
            {text(content, 'asks.task.assign', 'Assign a task')}
          </button>
        </div>
      )}

      {assigning && <AssignTaskDialog onClose={() => setAssigning(false)} />}

      {loading && <p className="text-[13px] text-ink-3">Loading&hellip;</p>}

      {mine.isError && (
        <p role="alert" className="mb-3 text-[13px] text-crit">
          {text(content, 'asks.failed.mine', 'Could not load your requests. Please try again.')}
        </p>
      )}

      {readsTheStudio && studio.isError && (
        <p role="alert" className="mb-3 text-[13px] text-crit">
          {text(content, 'asks.failed.studio', "Could not load the studio's requests. Please try again.")}
        </p>
      )}

      {nothing && (
        <p className="rounded-xl border border-line bg-surface p-4 text-[13px] text-ink-2">
          {text(content, 'asks.none', 'Nothing is waiting on anybody.')}
        </p>
      )}

      <Group
        heading={text(content, 'asks.waitingOnYou', 'Waiting on you')}
        rows={groups.waitingOnYou}
        content={content}
      />
      <Group
        heading={text(content, 'asks.chase', 'Running late, waiting on somebody else')}
        rows={groups.chase}
        content={content}
      />
      <Group
        heading={text(content, 'asks.youAreWaitingOn', 'You are waiting on')}
        rows={groups.youAreWaitingOn}
        content={content}
      />

      {/* The studio takes a weekend room back from here (D-713). */}
      {session?.capabilities.includes('ApproveBookings') && <WeekendHoldsPanel />}
    </div>
  )
}

function Group({ heading, rows, content }: { heading: string; rows: AskRowData[]; content: Content }) {
  // A heading over nothing is a promise with nothing behind it (D-415).
  if (rows.length === 0) return null

  return (
    <section className="mb-6">
      <h2 className="mb-2 font-display text-lg">{heading}</h2>
      <ul className="flex flex-col gap-2">
        {rows.map((row) => (
          <AskRow key={`${row.kind}:${row.id}`} row={row} content={content} />
        ))}
      </ul>
    </section>
  )
}
